import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Space } from '@cashfree-intl/coherent';

import { SubStepDescription } from 'containers/CheckoutJourney/screens/styled';
import { INTEGRATION_STATUS } from 'containers/CheckoutJourney/util/constant';
import ApiKeyModal from 'components/ApiKeyModal';
import {
  fetchAPIKeysAction,
  generateAPIKeyAction,
} from 'containers/ApiKeys/actions';
import ApiKeyReducer from 'containers/ApiKeys/reducer';
import ApiKeySaga from 'containers/ApiKeys/saga';
import makeSelectApiKeys from 'containers/ApiKeys/selectors';
import { showToast } from 'containers/Toast/actions';
import { sendAnalyticsData } from 'containers/ProductTour/helper';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectSaga } from 'utils/injectSaga';
import { exportToCSV } from 'utils/common';
import { SubstepComponentProps } from 'containers/CheckoutJourney/types';

const SandboxApiKeys = ({ substep, updateStatus }: SubstepComponentProps) => {
  const dispatch = useDispatch();
  const apiKeys = useSelector(makeSelectApiKeys());

  useInjectReducer({ key: 'apiKeys', reducer: ApiKeyReducer });
  useInjectSaga({ key: 'apiKeys', saga: ApiKeySaga, mode: '' });

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [keysNotGenerated, setKeysNotGenerated] = useState<boolean>(false);

  const { isLoading, data, apiKeyModalResponse } = apiKeys;

  useEffect(() => {
    dispatch(
      fetchAPIKeysAction({
        options: {},
        envCall: 'test',
        failureCb: err => {
          if (err && err.message === 'keys are not yet generated!') {
            setKeysNotGenerated(true);
          }
        },
      }),
    );
  }, [dispatch]);

  const handleButtonClick = () => {
    setIsGenerating(true);

    // If keys are not generated, generate them
    if (keysNotGenerated || !data || data.length === 0) {
      dispatch(
        generateAPIKeyAction({
          successCb: response => {
            setModalOpen(true);
            setIsGenerating(false);
            setKeysNotGenerated(false);
          },
          failureCb: (err: { message?: string }) => {
            setIsGenerating(false);
            if (err.message === 'Active Merchant key already exists') {
              dispatch(
                fetchAPIKeysAction({
                  options: {},
                  successCb: () => {
                    setModalOpen(true);
                    setKeysNotGenerated(false);
                  },
                  envCall: 'test',
                }),
              );
            } else {
              dispatch(
                showToast({
                  message: err.message || 'Error generating API key',
                  type: 'error',
                }),
              );
            }
          },
          envCall: 'test',
        }),
      );
    } else {
      setModalOpen(true);
      setIsGenerating(false);
    }

    sendAnalyticsData('procureSandboxAPIKeysButtonClicked');
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    updateStatus(substep.id, INTEGRATION_STATUS.COMPLETED);
  };

  const handleDownloadAPIKey = () => {
    exportToCSV('APIKey.csv', data);
    setModalOpen(false);
    updateStatus(substep.id, INTEGRATION_STATUS.COMPLETED);
  };

  const isButtonLoading = isLoading || isGenerating;

  const getModalData = () => {
    if (apiKeyModalResponse && Object.keys(apiKeyModalResponse).length > 0) {
      return apiKeyModalResponse;
    }
    if (data && data.length > 0) {
      return data[0];
    }
    return null;
  };

  return (
    <Space gap={2.5} direction="column" className="mt-2">
      <SubStepDescription color="bodyLight">
        {substep.description}
      </SubStepDescription>
      <Space direction="row" gap={1}>
        <Button
          primary
          onClick={handleButtonClick}
          loading={isButtonLoading}
          disabled={isButtonLoading}
        >
          Access API Keys
        </Button>
      </Space>
      {modalOpen && (
        <ApiKeyModal
          open={modalOpen}
          data={getModalData()}
          isViewMode={false}
          closeModal={handleCloseModal}
          downloadClick={handleDownloadAPIKey}
        />
      )}
    </Space>
  );
};

export { SandboxApiKeys };
